import React, { useState } from 'react';
import { PathMatch, useLocation, useMatch } from 'react-router-dom';
import * as LoginActions from '$/store/login/actions';
import { connect } from 'react-redux';
import * as Bixby from '$/core';
import { AppState } from '$/store';
import { CscpIntegrationService, LoggerService } from '$/services';
import Login from './Login';
import { LOGIN_WELCOME_MESSAGE, LOGIN_EMAIL_PLACEHOLDER, LOGIN_PASSWORD_PLACEHOLDER } from '$/core/constants';
import { Navigate } from 'react-router-dom';

const login = LoginActions.login;

interface ILoginRouteProps {
  from?: string;
}

interface ILoginContainerProps extends ILoginRouteProps {
  dispatchLogin: (LoginRequest: Bixby.Models.LoginRequest) => Promise<void>;
  isLoading: boolean;
  isAuthenticated: boolean;
  error?: string;
}

const LoginContainer: React.FC<ILoginContainerProps> = props => {
  const { isLoading, isAuthenticated, error, dispatchLogin } = props;
  const match: PathMatch<'from'> | null = useMatch('/login/:from?');
  const location = useLocation();
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [loginInProgress, setLoginInProgress] = useState(false);

  //Logic for redirects is different based on stand alone mode versus in iframe mode.
  const isStandAlone = Bixby.Configuration.isStandAloneMode;
  const readyForRedirect = isAuthenticated && !loginInProgress;

  let dynamicMatchFrom = '';
  if (readyForRedirect) {
    if (match && match.params.from) {
      LoggerService.log(`LOGIN CONTAINER: match.params.from has value ${match.params.from}`);
      dynamicMatchFrom = match.params.from;
    }
    // We must ignore the base path on redirect even though the match from catches it
    if (!isStandAlone && dynamicMatchFrom) {
      dynamicMatchFrom = dynamicMatchFrom.replace(Bixby.Configuration.cscpIframePathPrefix, '');
      // Our redirects won't have a prefix since we strip out the base path which has a trailing slash
      dynamicMatchFrom = `/${dynamicMatchFrom}`;
    }
    const pathForRedirectOnAuth = isStandAlone
      ? '/portfolio'
      : `/${CscpIntegrationService.getRedirectPath(readyForRedirect, location.pathname)}`;

    const target = dynamicMatchFrom ? decodeURIComponent(dynamicMatchFrom) : pathForRedirectOnAuth + location.hash;
    LoggerService.log(`LOGIN CONTAINER: new target ${target}`);
    return <Navigate to={target} />;
  }

  const handleEmailChange = (e: any) => {
    setEmail(e.target.value);
  };
  const handlePasswordChange = (e: any) => {
    setPassword(e.target.value);
  };
  const handleSubmit = async (e: any) => {
    e.preventDefault();
    setLoginInProgress(true);
    try {
      await dispatchLogin({
        email,
        password,
      });
    } catch {
      // error is handled via redux
    }
    setLoginInProgress(false);
  };

  return (
    <Login
      email={email}
      password={password}
      isLoading={loginInProgress || isLoading}
      onEmailChange={handleEmailChange}
      onPasswordChange={handlePasswordChange}
      onSubmit={handleSubmit}
      errorText={error}
      welcomeMessage={LOGIN_WELCOME_MESSAGE}
      emailPlaceholder={LOGIN_EMAIL_PLACEHOLDER}
      passwordPlaceholder={LOGIN_PASSWORD_PLACEHOLDER}
    />
  );
};

const mapStateToProps = (state: AppState) => {
  return {
    isLoading: state.loginState.isLoading,
    isAuthenticated: state.loginState.authenticated === true,
    error: state.loginState.error,
  };
};

const mapDispatchToProps = (dispatch: any) => ({
  dispatchLogin: (cmd: Bixby.Models.LoginRequest) => dispatch(login(cmd)),
});

export default connect(mapStateToProps, mapDispatchToProps)(LoginContainer);
